import React, { useCallback, useState } from 'react';
import { FaEuroSign, FaPercent } from 'react-icons/fa6';
import { MdLinkOff, MdOutlineSyncAlt } from 'react-icons/md';
import { PiEnvelopeSimple } from 'react-icons/pi';
import { useParams } from 'react-router-dom';
import { Button, Input, Textarea, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import { CustomBadge } from '../../../../../components/forms/CustomBadge';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import PhoneInput from '../../../../../components/forms/PhoneInputWrapper';
import useFetch from '../../../../../hooks/useFetch';
import FormGroup from '../../../../../layout/shared/form-group';
import { TutorService } from '../../../../../services/tutor/tutorService';
import { TutorApiResponse } from '../../../../../type/entities/tutor-type';
import cn from '../../../../../utils/classNames';
import { paymentMethods, tutorType } from '../../../TutorForm';
import { normalizeProvidingCorporationAvailability } from '../../../utils/providingCorporationNormalizer';
import TutorProfileLayout from '../../TutorProfileLayout';

interface TutorInfoProps { }

const TutorInfo: React.FC<TutorInfoProps> = () => {

    const { id = '' } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const service = new TutorService();
    const [syncingProductId, setSyncingProductId] = useState<string | null>(null);

    const [data, loading, , refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getTutorById(id);
        return response.getResponseData() as TutorApiResponse;
    }, [id]));

    const canManageProductAssignment = userCan('edit_tutors', 'tutors');

    const handleLinkProductAssignment = async (productId: string) => {
        if (!id || syncingProductId) return;

        setSyncingProductId(productId);
        try {
            const response = await (new TutorService()).linkTutorProductAssignment(id, productId);
            const responseData = response.getResponseData();

            if (responseData.success) {
                toast.success(responseData.message || 'Acción sincronizada correctamente');
                refetch();
            } else {
                handleErrors(responseData);
            }
        } catch (error) {
            toast.error('No se ha podido sincronizar la acción con el producto');
        } finally {
            setSyncingProductId(null);
        }
    };

    const handleUnlinkProductAssignment = async (productId: string) => {
        if (!id || syncingProductId) return;

        setSyncingProductId(productId);
        try {
            const response = await (new TutorService()).unlinkTutorProductAssignment(id, productId);
            const responseData = response.getResponseData();

            if (responseData.success) {
                toast.success(responseData.message || 'Acción desvinculada correctamente');
                refetch();
            } else {
                handleErrors(responseData);
            }
        } catch (error) {
            toast.error('No se ha podido desvincular la acción del producto');
        } finally {
            setSyncingProductId(null);
        }
    };

    return (
        <TutorProfileLayout isLoading={loading}>
            <div className="@container">
                <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11">
                    <FormGroup
                        title="Datos Personales"
                        description="Información personal del tutor"
                        className='pt-3 pb-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-5"
                    >
                        <Input
                            readOnly
                            label="Nombre"
                            placeholder="First Name"
                            value={data?.name ?? ""}
                            className="flex-grow"
                        />
                        <Input
                            readOnly
                            label="Apellidos"
                            placeholder="Last Name"
                            value={data?.lastName ?? ""}
                            className="flex-grow"
                        />
                        <Input
                            readOnly
                            label="DNI"
                            placeholder="12345678Z"
                            value={data?.nifCif ?? ""}
                            className="flex-grow"
                        />
                        <Input
                            prefix={<PiEnvelopeSimple className="h-6 w-6 text-gray-500" />}
                            type="email"
                            label="Email"
                            placeholder="info@attiva.es"
                            value={data?.email ?? ""}
                            className='flex-grow md:col-span-2'
                            readOnly
                        />
                        <Input
                            readOnly
                            label="IBAN"
                            placeholder="ES1234567891234567891234"
                            value={data?.iban ?? ""}
                            className="flex-grow"
                        />
                        <PhoneInput
                            id="telephone"
                            readOnly
                            label="Teléfono"
                            placeholder="123456789"
                            value={data?.telephone ?? ""}
                            className="flex-grow"
                            required={false}
                        />
                        <PhoneInput
                            id="telephoneSecondary"
                            readOnly
                            label="Teléfono secundario"
                            placeholder="123456789"
                            value={data?.telephoneSecondary ?? ""}
                            className="flex-grow"
                            required={false}
                        />
                        <CustomSelect
                            readOnly
                            id="tutorType"
                            label="Tipo de Tutor"
                            value={{
                                value: data?.tutorType,
                                label: tutorType.find((type) => type.value === data?.tutorType)?.label,
                            }}
                        />
                    </FormGroup>

                    <FormGroup
                        title="Empresas impartidoras"
                        description="Credenciales y disponibilidad por empresa impartidora"
                        className='pt-3 pb-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-1"
                    >
                        <div className="space-y-4">
                            {(data?.providingCorporations ?? []).map((pc: any) => {
                                const labelMap: any = { monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo' };
                                const availability = normalizeProvidingCorporationAvailability(pc);
                                return (
                                    <div key={pc.id} className="rounded-lg border border-gray-200 p-4 space-y-3">
                                        <div className="font-medium">{pc.providingCorporation?.name ?? 'Empresa impartidora'}</div>
                                        <div className="grid md:grid-cols-3 gap-3">
                                            <Input readOnly label="Usuario plataforma" value={pc.platformUser ?? ''} />
                                            <Input readOnly label="Contraseña plataforma" value={pc.platformPassword ?? ''} />
                                            <Input readOnly label="Requiere firma colaboración" value={pc.collaborationSignature ? 'Sí' : 'No'} />
                                            <Input readOnly label="Convenio firmado" value={pc.hasAgreementSigned ? 'Sí' : 'No'} />
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-3">
                                            {availability.length === 0 && (
                                                <div className="text-sm text-gray-500">Sin disponibilidad</div>
                                            )}
                                            {availability.map((day: any) => (
                                                <div key={day.dayOfWeek} className="rounded-md border border-gray-100 p-3">
                                                    <div className="font-medium text-sm">{labelMap[day.dayOfWeek] ?? day.dayOfWeek}</div>
                                                    <div className="mt-2 space-y-1">
                                                        {(day.slots ?? []).map((s: any, idx: number) => (
                                                            <div key={idx} className="text-sm text-gray-700">
                                                                {s.startTime}-{s.endTime}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}

                            {(data?.providingCorporations ?? []).length === 0 && (
                                <div className="text-sm text-gray-500">Este tutor no tiene empresas impartidoras asignadas.</div>
                            )}
                        </div>
                    </FormGroup>

                    <FormGroup
                        title="Acciones bonificadas"
                        description="Acciones formativas bonificadas que el tutor puede impartir"
                        className={cn('pt-3 pb-4', data?.products?.length === 0 && "hidden")}
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-2"
                    >
                        {(data?.products ?? []).map((product: any, index: number, arr: any[]) => (
                            <div
                                key={product.id}
                                className={cn(
                                    "space-y-4 rounded-lg border p-4 m-3 transition-colors",
                                    product.productHasTutorLinked
                                        ? "border-green-200 bg-green-50/60"
                                        : "border-amber-200 bg-amber-50/60",
                                    (index !== arr.length - 1 && (index + 1) % 3 !== 0) && "border-e"
                                )}
                            >
                                <div className="flex flex-col gap-3 border-b border-black/5 pb-3 lg:flex-row lg:items-start lg:justify-between">
                                    <div className="min-w-0 space-y-2">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <CustomBadge
                                                variant="flat"
                                                color={product.productHasTutorLinked ? 'success' : 'warning'}
                                                rounded="md"
                                                size="sm"
                                            >
                                                {product.productHasTutorLinked ? 'Disponible en selector' : 'Solo en ficha del tutor'}
                                            </CustomBadge>
                                            {product.productHasTutorLinked && product.productHasTutorPriority && (
                                                <CustomBadge variant="outline" color="secondary" rounded="md" size="sm">
                                                    Prioridad {product.productHasTutorPriority}
                                                </CustomBadge>
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-900 font-semibold leading-6">
                                            {product.product.code ? `${product.product.code} - ` : ''}{product.product.name}
                                        </div>
                                    </div>

                                    {canManageProductAssignment && (
                                        product.productHasTutorLinked ? (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                color="danger"
                                                className="shrink-0"
                                                isLoading={syncingProductId === product.product.id}
                                                onClick={() => handleUnlinkProductAssignment(product.product.id)}
                                            >
                                                <MdLinkOff className="h-4 w-4" />
                                                Desvincular del producto
                                            </Button>
                                        ) : (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="shrink-0"
                                                isLoading={syncingProductId === product.product.id}
                                                onClick={() => handleLinkProductAssignment(product.product.id)}
                                            >
                                                <MdOutlineSyncAlt className="h-4 w-4" />
                                                Vincular al producto
                                            </Button>
                                        )
                                    )}
                                </div>

                                <div className="flex flex-row flex-wrap gap-3">
                                    <Tooltip
                                        size="md"
                                        content={<div className='tooltip-container bg-primary text-white'>{product.product.name}</div>}
                                        placement="top"
                                        color="primary"
                                    >
                                        <Input
                                            readOnly
                                            label="Producto"
                                            placeholder="Producto"
                                            value={product.product.name}
                                            className="w-full md:col-span-2 flex-grow font-medium"
                                            labelClassName='text-md'
                                        />
                                    </Tooltip>
                                    {data?.tutorType === 'external' && (
                                        <>
                                            <Input
                                                readOnly
                                                label="Precio"
                                                placeholder="Precio"
                                                suffix={<FaEuroSign />}
                                                value={product.pricePerStudent}
                                                className="md:col-span-2 flex-grow"
                                            />
                                            <CustomSelect
                                                id="paymentMethod"
                                                label="Método de Pago"
                                                value={{
                                                    value: product.paymentMethod,
                                                    label: paymentMethods.find((type) => type.value === product.paymentMethod)?.label,
                                                }}
                                                containerClassName='md:col-span-2 flex-grow'
                                                readOnly
                                            />
                                            <Input
                                                readOnly
                                                label="Retenciones"
                                                placeholder="Retenciones"
                                                suffix={<FaPercent />}
                                                value={product.deductions}
                                                className="md:col-span-2 flex-grow"
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </FormGroup>

                    <FormGroup
                        title="Otros datos"
                        description="Información adicional de la cuenta"
                        className='pt-3 pb-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-5"
                    >
                        <Input
                            readOnly
                            label="Dirección"
                            placeholder="Calle de la forja, 123"
                            value={data?.address ?? ""}
                            className="flex-grow md:col-span-2"
                        />
                        <Input
                            readOnly
                            label="Población"
                            placeholder="Córdoba"
                            value={data?.town ?? ""}
                            className="flex-grow"
                        />
                        <Input
                            readOnly
                            label="Provincia"
                            placeholder="Madrid"
                            value={data?.province ?? ""}
                            className="flex-grow"
                        />
                        <Input
                            readOnly
                            label="Código Postal"
                            placeholder="12345"
                            value={data?.postalCode ?? ""}
                            className="flex-grow"
                        />
                        <Textarea
                            id="comments"
                            readOnly
                            label="Comentarios"
                            rows={3}
                            className="col-span-1 md:col-span-4 [&>label>span]:font-medium"
                            value={data?.comments}
                        />
                    </FormGroup>
                </div>
            </div>
        </TutorProfileLayout>
    );
};

export default TutorInfo;
